import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/apiService";

const TestTake = ({ questions, onSubmit, isSubmitting, timeLimit }) => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timeLimit || null);
  const [submitted, setSubmitted] = useState(false);

  // Handle timer
  useEffect(() => {
    if (!timeLimit) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLimit]);

  const handleAnswer = async (questionIndex, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  const processAnswers = async () => {
    try {
      const processedAnswers = [];
      const shortAnswerPromises = [];

      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        const answer = answers[i];

        if (question.type === "short-answer") {
          if (
            answer &&
            typeof answer === "string" &&
            answer.trim().length > 0
          ) {
            shortAnswerPromises.push(
              apiService
                .post("/api/ai/evaluate-short-answer", {
                  question: question.question,
                  studentAnswer: answer.trim(),
                  correctAnswer: question.correctAnswer,
                })
                .then((response) => ({
                  questionIndex: i,
                  answer: answer.trim(),
                  isCorrect: response === "right",
                }))
                .catch(() => ({
                  questionIndex: i,
                  answer: answer.trim(),
                  isCorrect: false,
                }))
            );
          } else {
            processedAnswers.push({
              questionIndex: i,
              answer: "",
              isCorrect: false,
            });
          }
        } else if (question.type === "multiple-choice") {
          const selectedLetter = answer?.split(":")[0]?.trim();
          processedAnswers.push({
            questionIndex: i,
            answer: answer || "",
            isCorrect: selectedLetter === question.correctAnswer,
          });
        } else {
          processedAnswers.push({
            questionIndex: i,
            answer: answer || "",
            isCorrect: answer === question.correctAnswer,
          });
        }
      }

      if (shortAnswerPromises.length > 0) {
        const shortAnswerResults = await Promise.all(shortAnswerPromises);
        processedAnswers.push(...shortAnswerResults);
      }

      return processedAnswers.sort((a, b) => a.questionIndex - b.questionIndex);
    } catch (error) {
      console.error("Error processing answers:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (submitted) return;
    setSubmitted(true);

    try {
      const processedAnswers = await processAnswers();
      await onSubmit(processedAnswers);
    } catch (error) {
      console.error("Error submitting test:", error);
      setSubmitted(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <button
          onClick={() => navigate("/test")}
          className="text-gray-400 hover:text-white flex items-center gap-2 mb-8"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to Tests
        </button>
      </div>

      {/* Test Content - Centered */}
      <div className="flex justify-center items-center px-4">
        <div className="w-full max-w-4xl pt-12">
          {/* Timer */}
          {timeLeft && (
            <div className="fixed top-4 right-4 bg-dark-card px-4 py-2 rounded-lg">
              <span className="text-white">
                Time Left: {formatTime(timeLeft)}
              </span>
            </div>
          )}

          {/* Question */}
          <div className="bg-dark-card rounded-xl p-6 mb-8">
            <div className="mb-4">
              <span className="text-gray-400">
                Question {currentQuestion + 1} of {questions.length}
              </span>
            </div>

            <h2 className="text-xl text-white mb-6">
              {questions[currentQuestion].question}
            </h2>

            {/* Answer Section */}
            {questions[currentQuestion].type === "multiple-choice" && (
              <div className="space-y-4">
                {questions[currentQuestion].options.map((option, index) => {
                  const optionLetter = option.split(":")[0].trim();
                  return (
                    <button
                      key={index}
                      onClick={() =>
                        handleAnswer(currentQuestion, optionLetter)
                      }
                      className={`w-full text-left p-4 rounded-lg transition-colors ${
                        answers[currentQuestion] === optionLetter
                          ? "bg-blue-500 text-white"
                          : "bg-dark-bg text-gray-300 hover:bg-dark-hover"
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            )}

            {questions[currentQuestion].type === "true-false" && (
              <div className="grid grid-cols-2 gap-4">
                {["True", "False"].map((option) => (
                  <button
                    key={option}
                    onClick={() => handleAnswer(currentQuestion, option)}
                    className={`w-full px-6 py-3 rounded-lg transition-colors ${
                      answers[currentQuestion] === option
                        ? "bg-blue-500 text-white"
                        : "bg-dark-bg text-gray-300 hover:bg-dark-hover"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

            {questions[currentQuestion].type === "short-answer" && (
              <textarea
                value={answers[currentQuestion] || ""}
                onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                className="w-full h-32 bg-dark-bg text-white p-4 rounded-lg resize-none"
                placeholder="Type your answer here..."
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={() =>
                setCurrentQuestion((prev) => Math.max(0, prev - 1))
              }
              disabled={currentQuestion === 0}
              className="px-6 py-2 rounded-lg bg-dark-card text-white disabled:opacity-50"
            >
              Previous
            </button>

            {currentQuestion < questions.length - 1 ? (
              <button
                onClick={() =>
                  setCurrentQuestion((prev) =>
                    Math.min(questions.length - 1, prev + 1)
                  )
                }
                className="px-6 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || submitted}
                className="px-6 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  "Submit Test"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestTake;
